import { Link } from 'react-router-dom'
import type { AffiliateConfig } from '@/api/client'
import { AFFILIATE_TONES } from '@/lib/coupang'
import { useAffiliateAvatars, useAffiliateCategories, useTargets } from '@/lib/queries'
import { Field, FieldGroup, Input, Segmented, Select } from '@/ui'

/** The form's own copy of AffiliateConfig. The two number fields stay text so
 *  an emptied field is told apart from a 0. */
export interface AffiliateDraft {
  categoryIds: number[]
  includeUnassigned: boolean
  tone: 'promo' | 'review'
  persona: string
  minScore: string
  staleHours: string
  attachImage: boolean
  autoApprove: boolean
  headsUp: boolean
}

export const BLANK_AFFILIATE: AffiliateDraft = {
  categoryIds: [],
  includeUnassigned: false,
  tone: 'promo',
  persona: '',
  minScore: '0',
  staleHours: '',
  attachImage: false,
  autoApprove: false,
  headsUp: true,
}

export function affiliateDraftOf(a?: AffiliateConfig | null): AffiliateDraft {
  if (!a) return BLANK_AFFILIATE
  return {
    categoryIds: a.category_ids ?? [],
    includeUnassigned: a.include_unassigned,
    tone: a.tone === 'review' ? 'review' : 'promo',
    persona: a.persona ?? '',
    minScore: String(a.min_signal_score ?? 0),
    staleHours: a.stale_age_hours ? String(a.stale_age_hours) : '',
    attachImage: a.attach_image,
    autoApprove: a.auto_approve,
    headsUp: a.heads_up ?? true,
  }
}

/** The first thing wrong with the form, in the words the server uses too, or
 *  null. `targetId` is the campaign's Threads account. */
export function affiliateError(d: AffiliateDraft, targetId: number): string | null {
  if (!targetId) return '쿠팡 캠페인은 글을 올릴 Threads 계정을 정해야 한다'
  if (d.categoryIds.length === 0 && !d.includeUnassigned) {
    return '쿠팡 카테고리를 하나 이상 고르거나, 담당 계정이 없는 카테고리를 함께 쓴다'
  }
  const score = d.minScore.trim() === '' ? NaN : Number(d.minScore)
  if (!(score >= 0 && score <= 100)) return '최저 시그널 점수는 0에서 100 사이로 적는다'
  if (d.staleHours.trim() !== '') {
    const h = Number(d.staleHours)
    if (!Number.isInteger(h) || h < 0) return '시그널 유효 시간은 0(설정값을 따름) 이상으로 적는다'
  }
  return null
}

export function affiliatePayload(d: AffiliateDraft): AffiliateConfig {
  return {
    category_ids: d.categoryIds,
    include_unassigned: d.includeUnassigned,
    tone: d.tone,
    persona: d.persona,
    min_signal_score: Number(d.minScore),
    stale_age_hours: d.staleHours.trim() === '' ? 0 : Number(d.staleHours),
    attach_image: d.attachImage,
    auto_approve: d.autoApprove,
    heads_up: d.headsUp,
  }
}

const CHECK = 'h-4 w-4 shrink-0 accent-[rgb(var(--accent))]'

/** What a Coupang campaign writes about and how. There is no topic pool and no
 *  ad-judge threshold here: posts come from signals and the judge does not
 *  run for this mode. */
export function AffiliateFields({
  value,
  onChange,
}: {
  value: AffiliateDraft
  onChange: (next: AffiliateDraft) => void
}) {
  const cats = useAffiliateCategories()
  const avatars = useAffiliateAvatars().data?.items ?? []
  const targets = useTargets().data ?? []
  const items = cats.data?.items ?? []
  const set = (patch: Partial<AffiliateDraft>) => onChange({ ...value, ...patch })
  const toggle = (id: number) =>
    set({ categoryIds: value.categoryIds.includes(id) ? value.categoryIds.filter((x) => x !== id) : [...value.categoryIds, id] })

  return (
    <div className="space-y-4">
      <fieldset className="min-w-0 space-y-2">
        <legend className="text-meta font-medium">카테고리</legend>
        {items.length === 0 ? (
          <p className="text-table text-muted-foreground">
            {cats.isPending ? '불러오는 중' : '추가한 카테고리가 없다.'}{' '}
            {!cats.isPending && (
              <Link to="/affiliate" className="text-accent hover:underline">
                쿠팡 페이지에서 추가한다.
              </Link>
            )}
          </p>
        ) : (
          <ul className="grid gap-1 sm:grid-cols-2" aria-label="카테고리">
            {items.map((c) => {
              const owner = targets.find((t) => t.id === c.default_target_id)?.name
              return (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-body hover:bg-muted/60">
                    <input type="checkbox" className={CHECK} checked={value.categoryIds.includes(c.id)} onChange={() => toggle(c.id)} />
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    <span className="shrink-0 text-meta text-muted-foreground">{owner ?? '공용'}</span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
        <label className="flex items-center gap-2 text-body">
          <input
            type="checkbox"
            className={CHECK}
            checked={value.includeUnassigned}
            onChange={(e) => set({ includeUnassigned: e.target.checked })}
          />
          지정 없는 카테고리도 쓰기
        </label>
        <p className="text-meta text-muted-foreground">
          계정이 정해지지 않은 카테고리(공용)의 상품도 이 캠페인이 쓴다. 한 상품은 어느 캠페인에서든 한 번만 올라간다.
        </p>
      </fieldset>

      <FieldGroup columns={2}>
        <Field label="글 톤" hint={AFFILIATE_TONES.find((t) => t.value === value.tone)?.note}>
          <Segmented
            label="글 톤"
            value={value.tone}
            onChange={(tone) => set({ tone })}
            options={AFFILIATE_TONES.map((t) => ({ value: t.value, label: t.label }))}
          />
        </Field>
        <Field label="말투" hint="고르지 않으면 계정에 정해 둔 말투를 따른다.">
          <Select value={value.persona} onChange={(e) => set({ persona: e.target.value })}>
            <option value="">계정 말투 따름</option>
            {avatars.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="최저 시그널 점수 (0–100)" hint="이 점수 아래의 글감은 쓰지 않는다. 0이면 가장 높은 것부터 쓴다.">
          <Input type="number" min={0} max={100} value={value.minScore} onChange={(e) => set({ minScore: e.target.value })} />
        </Field>
        <Field label="글감 유효 시간 (시간)" hint="비우면 설정의 상품 정보 유효 시간을 따른다.">
          <Input type="number" min={0} placeholder="설정값" value={value.staleHours} onChange={(e) => set({ staleHours: e.target.value })} />
        </Field>
      </FieldGroup>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-body">
          <input type="checkbox" className={CHECK} checked={value.attachImage} onChange={(e) => set({ attachImage: e.target.checked })} />
          상품 이미지 붙이기
        </label>
        <label className="flex items-center gap-2 text-body">
          <input type="checkbox" className={CHECK} checked={value.autoApprove} onChange={(e) => set({ autoApprove: e.target.checked })} />
          자동 승인
        </label>
        <p className="text-meta text-muted-foreground">점수 높은 글을 하루 글 수만큼 자동으로 예약한다.</p>
        <label className="flex items-center gap-2 text-body">
          <input type="checkbox" className={CHECK} checked={value.headsUp} onChange={(e) => set({ headsUp: e.target.checked })} />
          게시 30분 전 텔레그램 알림
        </label>
        <p className="text-meta text-muted-foreground">
          끄면 이 캠페인의 글은 알림 없이 올라간다. 게시 결과(성공·실패) 알림은 그대로 온다.
        </p>
      </div>
    </div>
  )
}
